import { useEffect, useRef } from 'react';

import { toast } from '@/components/ui/toast';
import type { RuntimeMode } from '@/features/wallet';

import { isV2SessionKey, useTradingStore } from '../store';

interface UseModeTransitionResetParams {
    runtimeMode: RuntimeMode;
    /**
     * The trading store's session key (`useAuth().sessionKey`): the JWT in V1, `v2:<address>` in V2 while the browser
     * wallet is unlocked. Leaving a `v2:` key is a wallet change. Omitted, only mode transitions reset.
     */
    sessionKey?: string | null;
    resetExecution: () => void;
    resetReconciliation: () => void;
}

/**
 * Detects runtime-mode transitions and resets all trading state.
 *
 * Owned-state resets (activePosition, tradeHistory, overlayTrade, betAmount,
 * chartPulse, demoBalance, recoveredSrcToken) are handled by
 * `store.resetForModeSwitch()`. External resets (execution engine,
 * reconciliation) are called directly.
 *
 * Demo only exists while signed out, so the only transitions are sign-in and
 * sign-out, and an open demo position dies at both. It used to vanish with no
 * explanation, which is worse than the position: the user is left wondering
 * whether a trade was placed. Signing in says so.
 *
 * V2 (browser wallet): a wallet change locks first (the browser wallet store's
 * `retireSigner`), so it reaches here as real → demo → real. Leaving a `v2:`
 * session key resets the execution engine and the reconciliation too, even
 * when no mode flip is seen (the store already dropped that wallet's slots in
 * `_syncAuth`): the execution hook's own slots must not outlive the wallet
 * they were opened on. A V1 token change (a refresh) never matches.
 */
export function useModeTransitionReset({
    runtimeMode,
    sessionKey = null,
    resetExecution,
    resetReconciliation,
}: UseModeTransitionResetParams): void {
    const prevModeRef = useRef(runtimeMode);
    const prevSessionKeyRef = useRef(sessionKey);

    useEffect(() => {
        const prevSessionKey = prevSessionKeyRef.current;
        prevSessionKeyRef.current = sessionKey;
        const leftV2Wallet =
            isV2SessionKey(prevSessionKey) && prevSessionKey !== sessionKey;
        const modeChanged = prevModeRef.current !== runtimeMode;
        if (!modeChanged && !leftV2Wallet) return;

        const hadDemoPosition =
            modeChanged &&
            prevModeRef.current === 'demo' &&
            useTradingStore.getState().activePosition !== null;

        prevModeRef.current = runtimeMode;
        if (modeChanged) {
            useTradingStore.getState().resetForModeSwitch();
        }
        if (leftV2Wallet) {
            useTradingStore.getState().resetForWalletChange();
        }
        resetReconciliation();
        resetExecution();

        if (hadDemoPosition && runtimeMode === 'real') {
            toast.info(
                'Demo position closed — you are trading with real funds now.',
            );
        }
    }, [runtimeMode, sessionKey, resetExecution, resetReconciliation]);
}
